import { useState } from 'react';
import { Plus, MoreVertical, Pencil, Copy, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalculatorTab } from '@/hooks/useCalculatorTabs';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HistoryPanel } from '@/components/HistoryPanel';
import { HistoryEntry } from '@/hooks/useGlobalHistory';

interface CalculatorTabsProps {
  tabs: CalculatorTab[];
  activeTabId: string;
  onTabChange: (id: string) => void;
  onAddTab: () => void;
  onRemoveTab: (id: string) => void;
  onDuplicateTab: (id: string) => void;
  onRenameTab: (id: string, name: string) => void;
  history?: HistoryEntry[];
  onSelectHistoryEntry?: (entry: HistoryEntry) => void;
  onClearHistory?: () => void;
  onDeleteHistoryEntry?: (id: string) => void;
}

export function CalculatorTabs({
  tabs,
  activeTabId,
  onTabChange,
  onAddTab,
  onRemoveTab,
  onDuplicateTab,
  onRenameTab,
  history = [],
  onSelectHistoryEntry,
  onClearHistory,
  onDeleteHistoryEntry,
}: CalculatorTabsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const startEditing = (tab: CalculatorTab, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingId(tab.id);
    setEditName(tab.name);
  };

  const saveEdit = () => {
    if (editingId && editName.trim()) {
      onRenameTab(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-4 scrollbar-hide -mx-1 px-1">
      <div className="flex items-center gap-2">
        <AnimatePresence mode="popLayout">
          {tabs.map((tab) => (
            <motion.div
              key={tab.id}
              layout
              initial={{ opacity: 0, scale: 0.9, x: -10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.9, x: -10 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={cn(
                "group relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer min-w-[100px] sm:min-w-[120px]",
                activeTabId === tab.id
                  ? "bg-primary/10 text-primary shadow-sm"
                  : "bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              )}
              onClick={() => onTabChange(tab.id)}
            >

              {editingId === tab.id ? (
                <div className="flex items-center gap-1 w-full">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-6 flex-1 text-xs px-2 bg-background"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label="Save tab name"
                    onClick={(e) => {
                      e.stopPropagation();
                      saveEdit();
                    }}
                  >
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-sm font-medium truncate flex-1">
                    {tab.name}
                  </span>
                  
                  {/* 3-dot dropdown menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Options for tab ${tab.name}`}
                        className={cn(
                          "h-6 w-6 shrink-0 transition-opacity",
                          activeTabId === tab.id 
                            ? "opacity-60 hover:opacity-100" 
                            : "opacity-0 group-hover:opacity-60 hover:!opacity-100"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="end" 
                      className="w-40 bg-popover border border-border shadow-lg"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenuItem 
                        onClick={(e) => startEditing(tab, e)}
                        className="cursor-pointer"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          onDuplicateTab(tab.id);
                        }}
                        className="cursor-pointer"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      {tabs.length > 1 && (
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveTab(tab.id);
                          }}
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Close
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            aria-label="Add new calculation tab"
            className="h-9 w-9 rounded-xl shrink-0 border-dashed hover:border-solid hover:border-primary hover:text-primary transition-all"
            onClick={onAddTab}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>New calculation</TooltipContent>
      </Tooltip>

      {/* Spacer to push history to the right */}
      <div className="flex-1" />

      {/* History Panel at the far right */}
      <HistoryPanel
        history={history}
        onSelectEntry={onSelectHistoryEntry}
        onClearHistory={onClearHistory}
        onDeleteEntry={onDeleteHistoryEntry}
      />
    </div>
  );
}
